import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import SplashBall from '../../../Splash/splashBall';
import ScatterSection from './ScatterSection';
import LastResults from './LastResults';
import HistogramSection from './HistogramSection';
import { trackEvent } from '../../../Analytics/TrackedClick';
import {
    performanceStatsAnalyticsEvents,
    performanceStatsEngagementTypes,
    getActivityStatsTraits,
} from '../../../../utils/analytics';
import usePerformanceStatsEngagement from './usePerformanceStatsEngagement';
import paths from '../../../../../constants/paths';
import useStartPowerCheckout from '../../../hooks/useStartPowerCheckout';
import { PowerFunnelSurfaces, PowerFunnelGatedFeatures } from '../../../Analytics/constants';
import {
    hasSinglesGraphData,
    resolveCurrentUtr,
    mapResultCards,
    LOCKED_PLACEHOLDER_SINGLES,
} from './performanceStatsUtils';
import styles from './performanceStats.scss';

function PerformanceStats({ auth, isOwnProfile, playerId, onViewAllResults, 'data-testid': dataTestId }) {
    const { performanceStats, data: profileData } = useSelector(({ profile }) => profile);
    const { startPowerCheckout } = useStartPowerCheckout();
    const trackEngagement = usePerformanceStatsEngagement();
    const { isLoading, data, error } = performanceStats;

    const isPowered = !!auth?.user?.isPowered;
    const isLocked = !isPowered || data?.isPowerUser === false;

    const launchPaywall = useCallback(() => {
        trackEvent(performanceStatsAnalyticsEvents.GET_POWER_CTA_TAPPED, getActivityStatsTraits(auth, playerId, isOwnProfile));
        startPowerCheckout({
            surface: PowerFunnelSurfaces.PLAYER_PROFILE,
            gatedFeature: PowerFunnelGatedFeatures.PERFORMANCE_STATS,
        });
    }, [startPowerCheckout, auth, playerId, isOwnProfile]);

    const singles = isLocked ? LOCKED_PLACEHOLDER_SINGLES : data?.graphs?.singles;
    const results = data?.results;
    const currentUtr = resolveCurrentUtr(singles, profileData?.singlesUtr);
    const resultCards = useMemo(() => mapResultCards(results, playerId), [results, playerId]);

    if (isLoading) {
        return (
            <div className={styles.performanceStatsContainer} data-testid={dataTestId}>
                <div className={styles.performanceStatsLoading} data-testid={dataTestId && `${dataTestId}.loading`}>
                    <SplashBall />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.performanceStatsContainer} data-testid={dataTestId}>
                <div className={styles.performanceStatsError} data-testid={dataTestId && `${dataTestId}.error`}>
                    Unable to retrieve performance stats
                </div>
            </div>
        );
    }

    if (data?.hasData === false || (!isLocked && !hasSinglesGraphData(data))) {
        return (
            <div className={styles.performanceStatsContainer} data-testid={dataTestId}>
                <div className="container">
                    <div className={styles.emptyState} data-testid={dataTestId && `${dataTestId}.empty-state`}>
                        <p className={styles.emptyStateText} data-testid={dataTestId && `${dataTestId}.empty-state.text`}>
                            Performance stats are not available because there are not enough match results.
                        </p>
                        {isOwnProfile && (
                            <div className={styles.emptyStateLinks}>
                                <Link to={paths.searchEvents} className={styles.emptyStateLink} data-testid={dataTestId && `${dataTestId}.empty-state.find-events-link`}>Find Events</Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const handleViewAllResults = () => {
        trackEngagement(performanceStatsEngagementTypes.CLICK_MORE_RESULTS);
        if (onViewAllResults) { onViewAllResults(); }
    };

    if (isLocked) {
        return (
            <div className={styles.performanceStatsContainer} data-testid={dataTestId}>
                <div className="container" data-testid={dataTestId && `${dataTestId}.locked`}>
                    <ScatterSection
                        singles={singles}
                        currentUtr={currentUtr}
                        profilePlayerId={playerId}
                        isLocked
                        onLockClick={launchPaywall}
                        data-testid={dataTestId && `${dataTestId}.locked.scatter`}
                    />
                    <LastResults
                        cards={resultCards}
                        onViewAll={handleViewAllResults}
                        isLocked
                        onLockClick={launchPaywall}
                        data-testid={dataTestId && `${dataTestId}.locked.last-results`}
                    />
                    <HistogramSection
                        singles={singles}
                        currentUtr={currentUtr}
                        isLocked
                        onLockClick={launchPaywall}
                        data-testid={dataTestId && `${dataTestId}.locked.histogram`}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.performanceStatsContainer} data-testid={dataTestId}>
            <div className="container">
                <ScatterSection
                    singles={singles}
                    currentUtr={currentUtr}
                    profilePlayerId={playerId}
                    data-testid={dataTestId && `${dataTestId}.scatter`}
                />
                <LastResults
                    cards={resultCards}
                    onViewAll={handleViewAllResults}
                    data-testid={dataTestId && `${dataTestId}.last-results`}
                />
                <HistogramSection
                    singles={singles}
                    currentUtr={currentUtr}
                    data-testid={dataTestId && `${dataTestId}.histogram`}
                />
            </div>
        </div>
    );
}

PerformanceStats.propTypes = {
    auth: PropTypes.shape({
        user: PropTypes.shape({
            isPowered: PropTypes.bool,
        }),
    }),
    isOwnProfile: PropTypes.bool,
    playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onViewAllResults: PropTypes.func,
    'data-testid': PropTypes.string,
};

PerformanceStats.defaultProps = {
    auth: null,
    isOwnProfile: false,
    playerId: null,
    onViewAllResults: null,
    'data-testid': null,
};

export default PerformanceStats;
